import { HttpErrorResponse } from '@angular/common/http';

/**
 * Normalized error used across the app. Produced by {@link parseHttpError} and consumed by the
 * ErrorService / interceptor. See docs/adr/0004-configurator-exception-handling-strategy.md and
 * .github/instructions/references/configurator-exceptions-frontend.md (§1–3).
 */
export interface AppError {
  /** HTTP status (0 for transport/network/CORS failures, or non-HTTP runtime errors). */
  status: number;
  /** Drives which display tier the ErrorService uses. */
  severity: ErrorSeverity;
  /** Short heading (modal title). */
  title: string;
  /** User-facing message (the ProblemDetails `detail`). Always safe to show — internal tool. */
  message: string;
  /** Optional machine-readable error code (ProblemDetails `code` extension). */
  code?: string | null;
  /** Ties the response back to backend telemetry/logs. Keep available for support. */
  correlationId?: string | null;
  /** Per-field validation messages (ProblemDetails `errors`). Maps to inline form errors. */
  fieldErrors?: Record<string, string[]>;
  /** Set for the tenant-db 404 (the customer exists but its database isn't provisioned here). */
  tenant?: { id: number; name: string | null; environment: string | null };
  /** Set for a 503 database-connection failure — which server/database/context could not be reached. */
  database?: {
    server: string | null;
    database: string | null;
    context: string | null;
    errorNumber: number | null;
  };
  /** True for transport/network/CORS failures and non-HTTP runtime errors (no response body). */
  isTransport: boolean;
  /** The original error (HttpErrorResponse or thrown value) for debugging — internal tool, safe to keep. */
  raw: unknown;
}

/**
 * Tiers, mapped per §6 of the reference doc:
 * - `validation`  → 400 with per-field `errors`; rendered inline on the form.
 * - `recoverable` → other recoverable 4xx (404/409, 400 without field errors); warning-styled modal.
 * - `blocking`    → 403 and the tenant-db 404; the user must acknowledge → modal.
 * - `redirect`    → 401; session expired → send to login.
 * - `fatal`       → 500 / unknown / transport (status 0); full-page `/error`.
 */
export type ErrorSeverity = 'validation' | 'recoverable' | 'blocking' | 'redirect' | 'fatal';

/** RFC 9457 ProblemDetails body (+ the configurator's extension members). */
interface ProblemDetailsBody {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
  code?: string | null;
  correlationId?: string | null;
  exception?: unknown;
  // Tenant-db extension members.
  tenantId?: number;
  environment?: string | null;
  customerName?: string | null;
  // SQL connection-failure extension member (503).
  database?: {
    server?: string | null;
    database?: string | null;
    context?: string | null;
    errorNumber?: number | null;
  };
}

/**
 * Parses any error into an {@link AppError}. The backend now emits **only** RFC 9457 ProblemDetails, so this
 * reads the canonical members (`status`, `detail`, `errors`, `code`, `correlationId`, and the tenant-db
 * members) and degrades gracefully when the body is NOT our ProblemDetails — a 401 from the auth layer, a
 * framework model-binding 400, a framework 404/405, or a transport failure (`status === 0`). See §5.
 */
export function parseHttpError(err: unknown): AppError {
  if (err instanceof HttpErrorResponse) {
    const status = err.status;
    const body = err.error;
    const isTransport = status === 0;

    if (isProblemDetails(body)) {
      const fieldErrors = normalizeFieldErrors(body.errors);
      const tenant = parseTenant(status, body);
      const database = parseDatabase(body);
      return {
        status,
        severity: severityFor(status, fieldErrors, tenant),
        title: body.title?.trim() || defaultTitle(status),
        message: body.detail?.trim() || body.title?.trim() || fallbackMessage(status),
        code: body.code ?? null,
        correlationId: body.correlationId ?? null,
        fieldErrors,
        tenant,
        database,
        isTransport,
        raw: err,
      };
    }

    // Not our ProblemDetails: framework model-binding errors, bare framework 404/405, empty 401/403
    // bodies, transport failures, or a plain-string body. Read defensively (only `status` is reliable).
    const message =
      typeof body === 'string' && body.trim() ? body.trim() : err.message || fallbackMessage(status);
    return {
      status,
      severity: severityFor(status, undefined, undefined),
      title: defaultTitle(status),
      message,
      correlationId: null,
      isTransport,
      raw: err,
    };
  }

  // Non-HTTP runtime error (handled by AppErrorHandler; parsed here for completeness).
  const message = (err as { message?: string } | null)?.message?.trim() || 'Der opstod en uventet fejl.';
  return {
    status: 0,
    severity: 'fatal',
    title: 'Der opstod en fejl',
    message,
    correlationId: null,
    isTransport: true,
    raw: err,
  };
}

function isProblemDetails(body: unknown): body is ProblemDetailsBody {
  return (
    !!body &&
    typeof body === 'object' &&
    ('detail' in body || 'title' in body || 'errors' in body || 'status' in body)
  );
}

function parseTenant(
  status: number,
  body: ProblemDetailsBody,
): AppError['tenant'] | undefined {
  if (status === 404 && typeof body.tenantId === 'number') {
    return { id: body.tenantId, name: body.customerName ?? null, environment: body.environment ?? null };
  }
  return undefined;
}

function parseDatabase(body: ProblemDetailsBody): AppError['database'] | undefined {
  const db = body.database;
  if (!db || typeof db !== 'object') {
    return undefined;
  }
  return {
    server: db.server ?? null,
    database: db.database ?? null,
    context: db.context ?? null,
    errorNumber: typeof db.errorNumber === 'number' ? db.errorNumber : null,
  };
}

function normalizeFieldErrors(errors?: Record<string, string[]>): Record<string, string[]> | undefined {
  if (!errors || typeof errors !== 'object') {
    return undefined;
  }
  const entries = Object.entries(errors).filter(([, msgs]) => Array.isArray(msgs) && msgs.length > 0);
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function severityFor(
  status: number,
  fieldErrors: Record<string, string[]> | undefined,
  tenant: AppError['tenant'] | undefined,
): ErrorSeverity {
  if (status === 401) {
    return 'redirect';
  }
  if (status === 400 && fieldErrors) {
    return 'validation';
  }
  if (status === 403 || tenant) {
    // Access denied, or the tenant-db 404 — blocking, the user must acknowledge.
    return 'blocking';
  }
  if (status === 0 || status >= 500) {
    return 'fatal';
  }
  // Remaining recoverable 4xx (incl. 400 without field errors, 404/409/422) — warning-styled modal.
  return 'recoverable';
}

function defaultTitle(status: number): string {
  if (status === 0) return 'Forbindelsesfejl';
  if (status === 400) return 'Ugyldig anmodning';
  if (status === 401) return 'Ikke logget ind';
  if (status === 403) return 'Adgang nægtet';
  if (status === 404) return 'Ikke fundet';
  if (status === 409) return 'Konflikt';
  if (status === 503) return 'Tjenesten er utilgængelig';
  if (status >= 500) return 'Serverfejl';
  return 'Der opstod en fejl';
}

function fallbackMessage(status: number): string {
  if (status === 0) return 'Kunne ikke få forbindelse til serveren.';
  if (status === 401) return 'Din session er udløbet. Log venligst ind igen.';
  if (status >= 500) return 'Der opstod en uventet fejl på serveren.';
  return 'Der opstod en fejl.';
}
